import React, { useEffect, useRef, useState } from 'react'
import {
	ActionIcon,
	Box,
	Button,
	ColorInput,
	FileInput,
	Grid,
	Group,
	MultiSelect,
	Space,
	Tabs,
	Text,
	TextInput,
	Textarea,
	Title,
	Tooltip,
} from '@mantine/core'
import {
	Backpack,
	BuildingSkyscraper,
	Calendar,
	ExternalLink,
	Headset,
	Help,
	InfoCircle,
	Link as LinkIcon,
	Luggage,
	Users,
} from 'tabler-icons-react'
import { useParams } from 'react-router-dom'
import {
	getExperience,
	getSegmentOptions,
	getStationOptions,
	updateExperience,
} from '../services/actions/experienceActions'
import { uploadImageToS3 } from '../services/utils/uploadImageToS3'
import { readFileAsDataUrl } from '../services/utils/imageCropper'
import CheckInPassPage from './CheckInPassPage'
import ImageCropperModal from '../components/ImageCropperModal'
import { getExperienceCustomFields } from '../services/actions/checkInActions'

const STRIP_IMAGE_TARGET_WIDTH = 1200
const STRIP_IMAGE_TARGET_HEIGHT = 320
const TRAVELER_HEADER_TARGET_WIDTH = 1600
const TRAVELER_HEADER_TARGET_HEIGHT = 720

const EXPERIENCE_LINK_ICONS = [
	{ value: 'trip', label: 'Trip details' },
	{ value: 'schedule', label: 'Schedule' },
	{ value: 'hotel', label: 'Hotel' },
	{ value: 'packing', label: 'Packing' },
	{ value: 'faq', label: 'FAQ' },
	{ value: 'contact', label: 'Contact' },
	{ value: 'chat', label: 'Group chat' },
	{ value: 'info', label: 'Information' },
	{ value: 'link', label: 'Link' },
]

const EXPERIENCE_LINK_ICON_COMPONENTS = {
	trip: Luggage,
	schedule: Calendar,
	hotel: BuildingSkyscraper,
	packing: Backpack,
	faq: Help,
	contact: Headset,
	chat: Users,
	info: InfoCircle,
	link: LinkIcon,
}

const normalizeCheckInTimesConfig = (value) =>
	(Array.isArray(value) ? value : [])
		.map((entry) => ({
			start: typeof entry?.start === 'string' ? entry.start : '',
			end: typeof entry?.end === 'string' ? entry.end : '',
			label: typeof entry?.label === 'string' ? entry.label : '',
		}))
		.filter((entry) => entry.start || entry.end || entry.label)

const normalizeExperienceLinksConfig = (value) =>
	(Array.isArray(value) ? value : [])
		.map((entry) => ({
			label: typeof entry?.label === 'string' ? entry.label : '',
			url: typeof entry?.url === 'string' ? entry.url : '',
			icon: typeof entry?.icon === 'string' ? entry.icon : 'link',
		}))
		.filter((entry) => entry.label || entry.url)

const normalizeEmbeddedPagesConfig = (value, validSegmentIds = null) =>
	(Array.isArray(value) ? value : [])
		.map((entry) => ({
			label: typeof entry?.label === 'string' ? entry.label : '',
			details: typeof entry?.details === 'string' ? entry.details : '',
			source: typeof entry?.source === 'string' ? entry.source : '',
			icon: typeof entry?.icon === 'string' ? entry.icon : 'link',
			segmentIds: Array.isArray(entry?.segmentIds)
				? entry.segmentIds.filter(
						(id) =>
							typeof id === 'string' &&
							(!validSegmentIds || validSegmentIds.has(id)),
					)
				: [],
		}))
		.filter((entry) => entry.label || entry.source)

const isValidExperienceLinkUrl = (value) => {
	try {
		const url = new URL(String(value || '').trim())
		return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)
	} catch {
		return false
	}
}

const ManagePortalPage = () => {
	const { orgSlug, experienceSlug } = useParams()
	const skipInitialSave = useRef(true)

	const [name, setName] = useState('')
	const [shortName, setShortName] = useState('')
	const [stationLabelOverride, setStationLabelOverride] = useState('')
	const [primaryColor, setPrimaryColor] = useState('#8F9297')
	const [textColor, setTextColor] = useState('#000000')
	const [labelColor, setLabelColor] = useState('#000000')
	const [stripImage, setStripImage] = useState('')
	const [travelerPageHeaderImage, setTravelerPageHeaderImage] = useState('')
	const [checkInTimes, setCheckInTimes] = useState([])
	const [experienceLinks, setExperienceLinks] = useState([])
	const [embeddedPages, setEmbeddedPages] = useState([])
	const [travelerPageCustomFields, setTravelerPageCustomFields] = useState([])
	const [customFieldOptions, setCustomFieldOptions] = useState([])
	const [experienceDetails, setExperienceDetails] = useState(null)
	const [stationOptions, setStationOptions] = useState([])
	const [segmentOptions, setSegmentOptions] = useState([])
	const [stripPreview, setStripPreview] = useState('')
	const [stripCropSourceUrl, setStripCropSourceUrl] = useState('')
	const [stripCropOpened, setStripCropOpened] = useState(false)
	const [uploadingStripImage, setUploadingStripImage] = useState(false)
	const [travelerHeaderPreview, setTravelerHeaderPreview] = useState('')
	const [travelerHeaderCropSourceUrl, setTravelerHeaderCropSourceUrl] =
		useState('')
	const [travelerHeaderCropOpened, setTravelerHeaderCropOpened] =
		useState(false)
	const [uploadingTravelerHeader, setUploadingTravelerHeader] = useState(false)
	const [hasLoaded, setHasLoaded] = useState(false)
	const [saveStatus, setSaveStatus] = useState('idle')
	const [lastSaved, setLastSaved] = useState(null)

	useEffect(() => {
		let cancelled = false
		skipInitialSave.current = true
		setHasLoaded(false)

		const fetchExperience = async () => {
			try {
				const [exp, stations, segments, availableCustomFields] = await Promise.all([
					getExperience(experienceSlug, orgSlug),
					getStationOptions(experienceSlug, orgSlug),
					getSegmentOptions(experienceSlug, orgSlug),
					getExperienceCustomFields(experienceSlug, orgSlug),
				])
				if (cancelled) return
				const normalizedStations = Array.isArray(stations) ? stations : []
				const normalizedSegments = Array.isArray(segments) ? segments : []

				setExperienceDetails(exp)
				setStationOptions(normalizedStations)
				setName(exp.name || '')
				setShortName(exp.shortName || '')
				setStationLabelOverride(exp.stationLabelOverride || '')
				setPrimaryColor(exp.primaryColor || '')
				setTextColor(exp.textColor || '')
				setLabelColor(exp.labelColor || '')
				setStripImage(exp.stripImage || '')
				setTravelerPageHeaderImage(exp.travelerPageHeaderImage || '')
				setCheckInTimes(normalizeCheckInTimesConfig(exp.checkInTimes))
				setExperienceLinks(normalizeExperienceLinksConfig(exp.links))
				const selectedCustomFields = Array.isArray(
					exp.travelerPageCustomFields,
				)
					? exp.travelerPageCustomFields.filter(
							(field) => typeof field === 'string' && field.trim(),
						)
					: []
				setTravelerPageCustomFields(selectedCustomFields)
				setCustomFieldOptions([
					...new Set([
						...(Array.isArray(availableCustomFields)
							? availableCustomFields
							: []),
						...selectedCustomFields,
					]),
				])
				setEmbeddedPages(
					normalizeEmbeddedPagesConfig(
						exp.embeddedPages,
						new Set(normalizedSegments.map((segment) => segment.id)),
					),
				)
				setSegmentOptions(normalizedSegments)
				setHasLoaded(true)
			} catch (err) {
				console.error(err)
				if (!cancelled) setSaveStatus('error')
			}
		}

		fetchExperience()
		return () => {
			cancelled = true
		}
	}, [experienceSlug, orgSlug])

	useEffect(() => {
		if (!hasLoaded) return
		if (skipInitialSave.current) {
			skipInitialSave.current = false
			return
		}

		setSaveStatus('saving')
		const timeout = setTimeout(async () => {
			try {
				await updateExperience(experienceSlug, orgSlug, {
					stationLabelOverride,
					primaryColor,
					textColor,
					labelColor,
					stripImage,
					travelerPageHeaderImage: travelerPageHeaderImage || null,
					travelerPageCustomFields,
					checkInTimes: checkInTimes
						.filter((entry) => entry.start && entry.end)
						.map(({ start, end, label }) => ({
							start,
							end,
							...(label.trim() ? { label: label.trim() } : {}),
						})),
					links: experienceLinks
						.filter(
							(entry) =>
								entry.label.trim() && isValidExperienceLinkUrl(entry.url),
						)
						.map(({ label, url, icon }) => ({
							label: label.trim(),
							url: url.trim(),
							icon,
						})),
					embeddedPages: embeddedPages
						.filter((entry) => entry.label.trim() && entry.source.trim())
						.map(({ label, details, source, icon, segmentIds }) => ({
							label: label.trim(),
							...(details.trim() ? { details: details.trim() } : {}),
							source: source.trim(),
							icon,
							segmentIds,
						})),
				})
				setSaveStatus('saved')
				setLastSaved(new Date())
			} catch (err) {
				console.error(err)
				setSaveStatus('error')
			}
		}, 500)

		return () => clearTimeout(timeout)
	}, [
		hasLoaded,
		stationLabelOverride,
		primaryColor,
		textColor,
		labelColor,
		stripImage,
		travelerPageHeaderImage,
		travelerPageCustomFields,
		checkInTimes,
		experienceLinks,
		embeddedPages,
		experienceSlug,
		orgSlug,
	])

	const handleSelectStripImage = async (file) => {
		if (!file) return
		try {
			const sourceUrl = await readFileAsDataUrl(file)
			setStripCropSourceUrl(sourceUrl)
			setStripCropOpened(true)
		} catch (err) {
			alert(err.message || 'Failed to load image for cropping')
		}
	}

	const handleRecropStripImage = () => {
		const current = stripPreview || stripImage
		if (!current) return
		setStripCropSourceUrl(current)
		setStripCropOpened(true)
	}

	const handleApplyStripImageCrop = async (blob) => {
		if (!blob) return
		try {
			setUploadingStripImage(true)
			const file = new File([blob], `pass-strip-${Date.now()}.jpg`, {
				type: 'image/jpeg',
			})
			const url = await uploadImageToS3(file)
			setStripImage(url)
			setStripPreview(url)
			setStripCropOpened(false)
		} catch (err) {
			alert(err.message || 'Upload failed')
		} finally {
			setUploadingStripImage(false)
		}
	}

	const handleRemoveStripImage = () => {
		setStripImage('')
		setStripPreview('')
		setStripCropSourceUrl('')
		setStripCropOpened(false)
	}

	const handleSelectTravelerHeader = async (file) => {
		if (!file) return
		try {
			const sourceUrl = await readFileAsDataUrl(file)
			setTravelerHeaderCropSourceUrl(sourceUrl)
			setTravelerHeaderCropOpened(true)
		} catch (err) {
			alert(err.message || 'Failed to load image for cropping')
		}
	}

	const handleRecropTravelerHeader = () => {
		const current = travelerHeaderPreview || travelerPageHeaderImage
		if (!current) return
		setTravelerHeaderCropSourceUrl(current)
		setTravelerHeaderCropOpened(true)
	}

	const handleApplyTravelerHeaderCrop = async (blob) => {
		if (!blob) return
		try {
			setUploadingTravelerHeader(true)
			const file = new File([blob], `traveler-header-${Date.now()}.jpg`, {
				type: 'image/jpeg',
			})
			const url = await uploadImageToS3(file)
			setTravelerPageHeaderImage(url)
			setTravelerHeaderPreview(url)
			setTravelerHeaderCropOpened(false)
		} catch (err) {
			alert(err.message || 'Upload failed')
		} finally {
			setUploadingTravelerHeader(false)
		}
	}

	const handleRemoveTravelerHeader = () => {
		setTravelerPageHeaderImage('')
		setTravelerHeaderPreview('')
		setTravelerHeaderCropSourceUrl('')
		setTravelerHeaderCropOpened(false)
	}

	const exampleStation = stationOptions[0] || null
	const previewExperienceId = experienceDetails?.id

	return (
		<Box px='md' py='md'>
			<Title order={2}>Experience Portal</Title>
			<Group spacing='xs' mb='md'>
				{saveStatus === 'saving' && <Text c='blue'>Saving...</Text>}
				{saveStatus === 'saved' && (
					<Text c='dimmed'>
						Saved{' '}
						{lastSaved &&
							`at ${lastSaved.toLocaleTimeString([], {
								hour: 'numeric',
								minute: '2-digit',
							})}`}
					</Text>
				)}
				{saveStatus === 'error' && <Text c='red'>Error saving</Text>}
			</Group>

			<Tabs defaultValue='pass' radius='md'>
				<Tabs.List grow>
					<Tabs.Tab value='pass'>Pass</Tabs.Tab>
					<Tabs.Tab value='traveler'>Experience Page</Tabs.Tab>
				</Tabs.List>

				<Tabs.Panel value='pass' pt='md'>
					<Grid gutter='xl' align='flex-start'>
						<Grid.Col span={12} md={6}>
							<Title order={4} mb='sm'>
								Pass Settings
							</Title>

							<ColorInput
								label='Background Color'
								value={primaryColor}
								onChange={setPrimaryColor}
								mb='sm'
							/>
							<ColorInput
								label='Label Color'
								value={labelColor}
								onChange={setLabelColor}
								mb='sm'
							/>
							<ColorInput
								label='Text Color'
								value={textColor}
								onChange={setTextColor}
								mb='sm'
							/>

							<TextInput
								label='Station Label Override'
								value={stationLabelOverride}
								onChange={(event) =>
									setStationLabelOverride(event.target.value)
								}
								mb='sm'
							/>
							<Text size='sm' c='dimmed' mt={-4}>
								Preview of traveler’s view:{' '}
								{stationLabelOverride
									? `"Your ${stationLabelOverride}"`
									: '"Your Check-In Location"'}
							</Text>

							<Space h='md' />

							<FileInput
								label={`Strip Image (${STRIP_IMAGE_TARGET_WIDTH}px by ${STRIP_IMAGE_TARGET_HEIGHT}px)`}
								placeholder='Upload strip image'
								accept='image/png,image/jpeg,image/webp'
								onChange={handleSelectStripImage}
							/>

							{(stripPreview || stripImage) && (
								<Box mt='sm'>
									<Group position='apart' align='center' mb={6}>
										<Text size='xs' c='dimmed'>
											Click image to re-crop
										</Text>
										<Button
											variant='subtle'
											color='red'
											size='xs'
											onClick={handleRemoveStripImage}
											disabled={uploadingStripImage}
										>
											Remove image
										</Button>
									</Group>
									<img
										src={stripPreview || stripImage}
										alt='Strip preview'
										onClick={handleRecropStripImage}
										style={{
											width: '100%',
											borderRadius: 8,
											objectFit: 'cover',
											cursor: 'pointer',
										}}
									/>
								</Box>
							)}
						</Grid.Col>

						<Grid.Col span={12} md={6}>
							<Group position='apart' align='center' mb='sm'>
								<Title order={4}>Pass Preview</Title>
								<Button
									component='a'
									href={
										previewExperienceId
											? `/get-pass/preview/${previewExperienceId}`
											: '#'
									}
									target='_blank'
									rel='noreferrer'
									leftIcon={<ExternalLink size={16} />}
									disabled={!previewExperienceId}
									size='xs'
									variant='light'
								>
									Open full preview
								</Button>
							</Group>
							<Box
								style={{
									border: '1px solid #dee2e6',
									borderRadius: 8,
									overflow: 'hidden',
									backgroundColor: '#f8f9fa',
								}}
							>
								<CheckInPassPage
									previewMode
									mockData={{
										id: 'preview-id',
										experiencePrimaryColor: primaryColor,
										experienceTextColor: textColor,
										experienceLabelColor: labelColor,
										experienceStripImage: stripPreview || stripImage,
										experienceName: name || 'Example Experience',
										shortName,
										orgLogo: null,
										travelerName: 'John Example',
										travelerEmail: 'john@example.com',
										travelerPhone: '(555) 555-5555',
										stationLabelOverride,
										station: exampleStation || { name: 'Main Entrance' },
										scheduledCheckInDate:
											experienceDetails?.startDate || new Date(),
									}}
								/>
							</Box>
						</Grid.Col>
					</Grid>
				</Tabs.Panel>

				<Tabs.Panel value='traveler' pt='md'>
					<Group position='apart' align='center' mb='sm'>
						<Title order={4}>Experience Page Branding</Title>
						<Button
							component='a'
							href={
								previewExperienceId
									? `/myexperience/preview/${previewExperienceId}`
									: '#'
							}
							target='_blank'
							rel='noreferrer'
							leftIcon={<ExternalLink size={16} />}
							disabled={!previewExperienceId}
							size='xs'
							variant='light'
						>
							Open full preview
						</Button>
					</Group>

					<FileInput
						label={`Header Image (${TRAVELER_HEADER_TARGET_WIDTH}px by ${TRAVELER_HEADER_TARGET_HEIGHT}px)`}
						description='Defaults to the pass strip image, then to a gradient using the pass primary color.'
						placeholder='Upload experience page header'
						accept='image/png,image/jpeg,image/webp'
						onChange={handleSelectTravelerHeader}
						disabled={uploadingTravelerHeader}
					/>

					{(travelerHeaderPreview || travelerPageHeaderImage) && (
						<Box mt='sm'>
							<Group position='apart' align='center' mb={6}>
								<Text size='xs' c='dimmed'>
									Click image to re-crop
								</Text>
								<Button
									variant='subtle'
									color='red'
									size='xs'
									onClick={handleRemoveTravelerHeader}
									disabled={uploadingTravelerHeader}
								>
									Remove image
								</Button>
							</Group>
							<img
								src={travelerHeaderPreview || travelerPageHeaderImage}
								alt='Experience page header preview'
								onClick={handleRecropTravelerHeader}
								style={{
									width: '100%',
									maxHeight: 280,
									borderRadius: 8,
									objectFit: 'cover',
									cursor: 'pointer',
								}}
							/>
						</Box>
					)}

					{!travelerPageHeaderImage && !travelerHeaderPreview && (
						<Text size='xs' c='dimmed' mt='xs'>
							Currently using:{' '}
							{stripImage ? 'pass strip image' : 'primary color gradient'}
						</Text>
					)}

					<Space h='xl' />
					<Title order={4} mb={4}>
						Check-in Info Custom Fields
					</Title>
					<Text size='sm' c='dimmed' mb='md'>
						Select uploaded check-in fields to show travelers in the Check-in
						Info tile. Only fields with a value for that traveler are shown.
					</Text>
					<MultiSelect
						label='Visible custom fields'
						placeholder={
							customFieldOptions.length
								? 'Select custom fields'
								: 'No uploaded custom fields found'
						}
						value={travelerPageCustomFields}
						onChange={setTravelerPageCustomFields}
						data={customFieldOptions.map((field) => ({
							value: field,
							label: field,
						}))}
						searchable
						clearable
						disabled={customFieldOptions.length === 0}
					/>

					<Space h='xl' />
					<Title order={4} mb={4}>
						Check-in Times
					</Title>
					<Text size='sm' c='dimmed' mb='md'>
						Add the time windows travelers can use to check in.
					</Text>

					{checkInTimes.map((entry, index) => (
						<Grid key={`check-in-time-${index}`} gutter='sm' mb='xs'>
							<Grid.Col span={12} sm={3}>
								<TextInput
									label='From'
									type='time'
									value={entry.start}
									onChange={(event) =>
										setCheckInTimes((current) =>
											current.map((item, itemIndex) =>
												itemIndex === index
													? { ...item, start: event.target.value }
													: item,
											),
										)
									}
								/>
							</Grid.Col>
							<Grid.Col span={12} sm={3}>
								<TextInput
									label='To'
									type='time'
									value={entry.end}
									onChange={(event) =>
										setCheckInTimes((current) =>
											current.map((item, itemIndex) =>
												itemIndex === index
													? { ...item, end: event.target.value }
													: item,
											),
										)
									}
								/>
							</Grid.Col>
							<Grid.Col span={12} sm={4}>
								<TextInput
									label='Label (optional)'
									placeholder='Early arrival'
									value={entry.label}
									onChange={(event) =>
										setCheckInTimes((current) =>
											current.map((item, itemIndex) =>
												itemIndex === index
													? { ...item, label: event.target.value }
													: item,
											),
										)
									}
								/>
							</Grid.Col>
							<Grid.Col span={12} sm={2}>
								<Button
									fullWidth
									color='red'
									variant='subtle'
									mt={25}
									onClick={() =>
										setCheckInTimes((current) =>
											current.filter((_, itemIndex) => itemIndex !== index),
										)
									}
								>
									Remove
								</Button>
							</Grid.Col>
						</Grid>
					))}

					<Button
						variant='light'
						onClick={() =>
							setCheckInTimes((current) => [
								...current,
								{ start: '12:00', end: '18:00', label: '' },
							])
						}
					>
						Add check-in time
					</Button>

					<Space h='xl' />
					<Title order={4} mb={4}>
						Useful Links
					</Title>
					<Text size='sm' c='dimmed' mb='md'>
						These links appear as quick actions on the traveler page.
					</Text>

					{experienceLinks.map((entry, index) => (
						<Grid key={`experience-link-${index}`} gutter='sm' mb='xs'>
							<Grid.Col span={12} sm={3}>
								<TextInput
									label='Label'
									placeholder='Trip details'
									value={entry.label}
									onChange={(event) =>
										setExperienceLinks((current) =>
											current.map((item, itemIndex) =>
												itemIndex === index
													? { ...item, label: event.target.value }
													: item,
											),
										)
									}
								/>
							</Grid.Col>
							<Grid.Col span={12} sm={7}>
								<TextInput
									label='URL'
									placeholder='https://…'
									value={entry.url}
									onChange={(event) =>
										setExperienceLinks((current) =>
											current.map((item, itemIndex) =>
												itemIndex === index
													? { ...item, url: event.target.value }
													: item,
											),
										)
									}
								/>
							</Grid.Col>
							<Grid.Col span={12} sm={2}>
								<Button
									fullWidth
									color='red'
									variant='subtle'
									mt={25}
									onClick={() =>
										setExperienceLinks((current) =>
											current.filter((_, itemIndex) => itemIndex !== index),
										)
									}
								>
									Remove
								</Button>
							</Grid.Col>
							<Grid.Col span={12}>
								<Text size='sm' weight={500} mb={6}>
									Icon
								</Text>
								<Group spacing={8}>
									{EXPERIENCE_LINK_ICONS.map((option) => {
										const Icon =
											EXPERIENCE_LINK_ICON_COMPONENTS[option.value] || LinkIcon
										const selected = entry.icon === option.value

										return (
											<Tooltip label={option.label} key={option.value}>
												<ActionIcon
													size='lg'
													variant={selected ? 'filled' : 'light'}
													color={selected ? 'blue' : 'gray'}
													aria-label={`Use ${option.label} icon`}
													onClick={() =>
														setExperienceLinks((current) =>
															current.map((item, itemIndex) =>
																itemIndex === index
																	? { ...item, icon: option.value }
																	: item,
															),
														)
													}
												>
													<Icon size={21} strokeWidth={1.8} />
												</ActionIcon>
											</Tooltip>
										)
									})}
								</Group>
							</Grid.Col>
						</Grid>
					))}

					<Button
						variant='light'
						onClick={() =>
							setExperienceLinks((current) => [
								...current,
								{ label: '', url: '', icon: 'link' },
							])
						}
					>
						Add useful link
					</Button>

					<Space h='xl' />
					<Title order={4} mb={4}>
						Embedded Pages
					</Title>
					<Text size='sm' c='dimmed' mb='md'>
						Paste iframe or embed HTML. Travelers open these pages inside their
						Experience Portal without leaving it.
					</Text>

					{embeddedPages.map((entry, index) => (
						<Box
							key={`embedded-page-${index}`}
							p='md'
							mb='sm'
							style={{
								border: '1px solid #dee2e6',
								borderRadius: 8,
							}}
						>
							<Grid gutter='sm'>
								<Grid.Col span={12} sm={10}>
									<TextInput
										label='Label'
										placeholder='Weekend itinerary'
										value={entry.label}
										onChange={(event) =>
											setEmbeddedPages((current) =>
												current.map((item, itemIndex) =>
													itemIndex === index
														? { ...item, label: event.target.value }
														: item,
												),
											)
										}
									/>
								</Grid.Col>
								<Grid.Col span={12} sm={2}>
									<Button
										fullWidth
										color='red'
										variant='subtle'
										mt={25}
										onClick={() =>
											setEmbeddedPages((current) =>
												current.filter((_, itemIndex) => itemIndex !== index),
											)
										}
									>
										Remove
									</Button>
								</Grid.Col>
								<Grid.Col span={12}>
									<TextInput
										label='Details'
										placeholder='View weekend itinerary'
										value={entry.details}
										onChange={(event) =>
											setEmbeddedPages((current) =>
												current.map((item, itemIndex) =>
													itemIndex === index
														? { ...item, details: event.target.value }
														: item,
												),
											)
										}
									/>
								</Grid.Col>
								<Grid.Col span={12}>
									<Textarea
										label='Source HTML'
										description='Paste the complete embed code, such as an iframe.'
										placeholder='<iframe src="https://…" />'
										value={entry.source}
										onChange={(event) =>
											setEmbeddedPages((current) =>
												current.map((item, itemIndex) =>
													itemIndex === index
														? { ...item, source: event.target.value }
														: item,
												),
											)
										}
										autosize
										minRows={4}
										maxRows={12}
										styles={{
											input: {
												fontFamily:
													'SFMono-Regular, Consolas, Liberation Mono, monospace',
												fontSize: 13,
											},
										}}
									/>
								</Grid.Col>
								<Grid.Col span={12}>
									<MultiSelect
										label='Segments'
										description='Optional. Leave empty to show this page to every traveler.'
										placeholder='All segments'
										value={entry.segmentIds}
										onChange={(segmentIds) =>
											setEmbeddedPages((current) =>
												current.map((item, itemIndex) =>
													itemIndex === index
														? { ...item, segmentIds }
														: item,
												),
											)
										}
										data={segmentOptions.map((segment) => ({
											value: segment.id,
											label: segment.name,
										}))}
										searchable
										clearable
									/>
								</Grid.Col>
								<Grid.Col span={12}>
									<Text size='sm' weight={500} mb={6}>
										Icon
									</Text>
									<Group spacing={8}>
										{EXPERIENCE_LINK_ICONS.map((option) => {
											const Icon =
												EXPERIENCE_LINK_ICON_COMPONENTS[option.value] ||
												LinkIcon
											const selected = entry.icon === option.value

											return (
												<Tooltip label={option.label} key={option.value}>
													<ActionIcon
														size='lg'
														variant={selected ? 'filled' : 'light'}
														color={selected ? 'blue' : 'gray'}
														aria-label={`Use ${option.label} icon`}
														onClick={() =>
															setEmbeddedPages((current) =>
																current.map((item, itemIndex) =>
																	itemIndex === index
																		? { ...item, icon: option.value }
																		: item,
																),
															)
														}
													>
														<Icon size={21} strokeWidth={1.8} />
													</ActionIcon>
												</Tooltip>
											)
										})}
									</Group>
								</Grid.Col>
							</Grid>
						</Box>
					))}

					<Button
						variant='light'
						onClick={() =>
							setEmbeddedPages((current) => [
								...current,
								{
									label: '',
									details: '',
									source: '',
									icon: 'link',
									segmentIds: [],
								},
							])
						}
						>
							Add embedded page
						</Button>
					</Tabs.Panel>
			</Tabs>

			<ImageCropperModal
				opened={stripCropOpened}
				onClose={() => setStripCropOpened(false)}
				title='Crop Strip Image'
				sourceUrl={stripCropSourceUrl}
				targetWidth={STRIP_IMAGE_TARGET_WIDTH}
				targetHeight={STRIP_IMAGE_TARGET_HEIGHT}
				onApply={handleApplyStripImageCrop}
				applyLabel='Use Cropped Strip'
				processing={uploadingStripImage}
			/>
			<ImageCropperModal
				opened={travelerHeaderCropOpened}
				onClose={() => setTravelerHeaderCropOpened(false)}
				title='Crop Experience Page Header'
				sourceUrl={travelerHeaderCropSourceUrl}
				targetWidth={TRAVELER_HEADER_TARGET_WIDTH}
				targetHeight={TRAVELER_HEADER_TARGET_HEIGHT}
				onApply={handleApplyTravelerHeaderCrop}
				applyLabel='Use Header Image'
				processing={uploadingTravelerHeader}
			/>
		</Box>
	)
}

export default ManagePortalPage
